import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { of } from 'rxjs';
import { QueryResult } from '../../pages/home/components/license/types/licenseTypes';
import { LicenseStatus } from '../../types/license';

@Injectable({
  providedIn: 'root',
})
export class LicenseService {
  public licenseSettings$ = signal<any>(null);
  public licenseVersion$ = signal<any>(null);
  public remoteSourcesList$ = signal<any>(null);

  private _settingsCache = new Map<string, any[]>();
  private _versionCache = new Map<string, any[]>();

  public readonly statusOptions$ = [
    { name: 'Ok', code: LicenseStatus.OK },
    { name: 'Payment Error', code: LicenseStatus.PAYMENT_ERROR },
    { name: 'Disabled', code: LicenseStatus.DISABLED },
  ];

  constructor(private _http: HttpClient) {}

  public getStatusName(code: string) {
    return this.statusOptions$.find((status) => status.code === code)?.name;
  }

  public getStatusStyles(code: string) {
    switch (code) {
      case LicenseStatus.OK:
        return 'success';
      case LicenseStatus.PAYMENT_ERROR:
        return 'danger';
      case LicenseStatus.DISABLED:
        return 'secondary';
    }

    return undefined;
  }

  public addLicenseToCompany(
    license: any,
    companyId: string,
  ): Promise<QueryResult> {
    const { name, address, status, authAddress } = license.form.value;
    const newLicense = {
      name,
      address,
      status: status.code,
      authAddress,
      company_id: companyId,
    };

    return new Promise((resolve) => {
      this._http.post('license/create', newLicense).subscribe({
        next: (res: any) => {
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
          return of(error);
        },
      });
    });
  }

  public editLicense(license: any, licenseId: string, companyId: string) {
    const { status } = license.form.value;
    const updatedLicense = {
      ...license.form.value,
      status: status.code,
      company_id: companyId,
    };

    return new Promise((resolve) => {
      this._http.put(`license/${licenseId}`, updatedLicense).subscribe({
        next: (res: any) => {
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
          return of(error);
        },
      });
    });
  }

  public updateSettings(settings: any, type: string, licenseId: string) {
    return new Promise((resolve) => {
      this._http
        .post(`license/${licenseId}/settings`, {
          type,
          content: settings,
        })
        .subscribe({
          next: (res: any) => {
            this._settingsCache.set(licenseId, res);
            resolve(QueryResult.ok);
          },
          error: (error: any) => {
            resolve(QueryResult.error);
            console.error(error);
          },
        });
    });
  }

  public updateLicenseStatus(licenseId: string, status: LicenseStatus) {
    return new Promise((resolve) => {
      this._http.put(`license/${licenseId}`, { status }).subscribe({
        next: () => {
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
          console.error(error);
        },
      });
    });
  }

  public deleteLicense(licenseId: string) {
    return new Promise((resolve) => {
      this._http.delete('license/' + licenseId).subscribe({
        next: () => {
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          resolve(QueryResult.error);
          console.error(error);
        },
      });
    });
  }

  public clearSettingsCache(licenseId: string) {
    this._settingsCache.delete(licenseId);
  }

  public fetchLicenseSettings(licenseId: string) {
    return new Promise((resolve) => {
      if (this._settingsCache.has(licenseId)) {
        this.licenseSettings$.set(this._settingsCache.get(licenseId));
        resolve(QueryResult.ok);
        return;
      }

      this._http.get(`license/${licenseId}/settings/`).subscribe({
        next: (res: any) => {
          res = res.reduce((acc: any, curr: any) => {
            acc[curr.type] = curr;
            return acc;
          }, {});
          this.licenseSettings$.set(res);
          this._settingsCache.set(licenseId, res);
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          this.licenseSettings$.set(null);
          // Clear cache for this license to prevent stale data
          this._settingsCache.delete(licenseId);
          resolve(QueryResult.error);
          console.error(error);
        },
      });
    });
  }

  public fetchLicenseVersion(licenseId: string) {
    return new Promise((resolve) => {
      if (this._versionCache.has(licenseId)) {
        this.licenseVersion$.set(this._versionCache.get(licenseId));
        resolve(QueryResult.ok);
        return;
      }

      this._http.get(`license/${licenseId}/version/`).subscribe({
        next: (res: any) => {
          this.licenseVersion$.set(res);
          this._versionCache.set(licenseId, res);
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          this.licenseVersion$.set(null);
          // Clear cache for this license to prevent stale data
          this._versionCache.delete(licenseId);
          resolve(QueryResult.error);
          console.error(error);
        },
      });
    });
  }

  public fetchRemoteSourcesList(licenseId: string) {
    return new Promise((resolve) => {
      this._http.get(`license/${licenseId}/remote-sources/`).subscribe({
        next: (res: any) => {
          this.remoteSourcesList$.set(res);
          resolve(QueryResult.ok);
        },
        error: (error: any) => {
          this.remoteSourcesList$.set(null);
          resolve(QueryResult.error);
          console.error(error);
        },
      });
    });
  }

  public createRemoteSource(remoteSource: any, licenseId: string) {
    return new Promise((resolve) => {
      this._http
        .post(`license/${licenseId}/remote-sources/create/`, remoteSource)
        .subscribe({
          next: (res: any) => {
            resolve(QueryResult.ok);
          },
          error: (error: any) => {
            resolve(QueryResult.error);
            console.error(error);
          },
        });
    });
  }
}
